import React, { useState } from 'react';
import { graphBuilderApi } from '../../services/api';
import { getCurrentUser } from '../../utils/roleCheck';
import './CopyToEnvironmentPage.css';

function ImportGraphModal({ onClose, onImportComplete }) {
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importName, setImportName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const stripBundleExtension = (filename) => {
    return filename
      .replace(/\.ndjson\.gz$/i, '')
      .replace(/\.gz$/i, '');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportPreview(null);
    setImportResult(null);
    setError(null);
    setImportName(stripBundleExtension(file.name));

    setPreviewLoading(true);
    try {
      const preview = await graphBuilderApi.graphTransferPreview(file);
      setImportPreview(preview);
    } catch (err) {
      setError(`Preview failed: ${err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      const user = getCurrentUser();
      const result = await graphBuilderApi.graphTransferImportLocal(
        importFile,
        importName || undefined,
        user?.userId
      );
      setImportResult(result);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="import-graph-modal-overlay" onClick={onClose}>
      <div className="import-graph-modal" onClick={(e) => e.stopPropagation()}>
        <div className="import-graph-modal-header">
          <h2>Import Graph</h2>
          <button className="import-graph-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="import-graph-modal-body">
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              {error}
              <button
                onClick={() => setError(null)}
                style={{ marginLeft: 12, cursor: 'pointer', background: 'none', border: 'none', fontWeight: 600 }}
              >
                &times;
              </button>
            </div>
          )}

          <p className="cte-description">
            Upload a previously exported <code>.ndjson.gz</code> bundle to import it into this environment.
            The original graph ID and workspace are preserved.
          </p>

          <div className="cte-form">
            <div className="cte-form-row">
              <label>Bundle File</label>
              <input
                type="file"
                accept=".gz,.ndjson.gz"
                onChange={handleFileSelect}
                disabled={importing}
              />
            </div>

            {previewLoading && (
              <p className="cte-muted">Loading preview...</p>
            )}

            {importPreview && (
              <div className="cte-preview">
                <h4>Bundle Preview</h4>
                <p>Graph ID: <code>{importPreview.manifest?.graph_id}</code></p>
                <p>Exported: {importPreview.manifest?.exported_at}</p>
                <div className="cte-preview-counts">
                  {Object.entries(importPreview.record_counts || {}).map(([k, v]) => (
                    <span key={k} className="cte-preview-count">{k}: {v}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="cte-form-row">
              <label>Graph Name</label>
              <input
                type="text"
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                placeholder="Derived from filename"
                disabled={importing}
              />
            </div>

            <button
              className="btn-primary"
              onClick={handleImport}
              disabled={importing || !importFile}
            >
              {importing ? 'Importing...' : 'Import'}
            </button>
          </div>

          {importResult && (
            <div className={`cte-result ${importResult.status === 'ok' ? 'cte-result-success' : 'cte-result-warning'}`}>
              <h4>Import {importResult.status === 'ok' ? 'Complete' : 'Completed with Warnings'}</h4>
              <p>Graph ID: <code>{importResult.new_graph_id}</code></p>
              <p>Graph Name: <strong>{importResult.new_graph_name}</strong></p>
              <p>
                Rows: {importResult.total_rows?.toLocaleString()} | Vectors: {importResult.total_vectors?.toLocaleString()}
              </p>
              {importResult.errors?.length > 0 && (
                <div className="cte-errors">
                  <strong>Warnings:</strong>
                  {importResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportGraphModal;
